import React, { ReactNode, useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Table } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'

import { FieldFragment } from '../../../types/graphql'

import { fieldHasNoInput } from './util'

type Row = {
  id: string
  values: ReactNode[]
}

type Props = {
  fieldTemplates: FieldFragment[]
  isLoading?: boolean
  rows: Row[]
}

const FieldsDisplayTable = ({ fieldTemplates, rows, isLoading }: Props) => {
  const filteredFields = useMemo(() => {
    const fieldTemplatesWithOriginalIndex = fieldTemplates.map((f, index) => ({
      fieldTemplate: f,
      index: index,
    }))
    return fieldTemplatesWithOriginalIndex.filter((f) => !fieldHasNoInput(f.fieldTemplate.type))
  }, [fieldTemplates])

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<Row>()
    return filteredFields.map((fieldTemplateWithIndex) =>
      columnHelper.display({
        id: `${fieldTemplateWithIndex.fieldTemplate.id}`,
        header: fieldTemplateWithIndex.fieldTemplate.label ?? '',
        cell: ({ row }) => row.original.values[fieldTemplateWithIndex.index] ?? null,
      })
    )
  }, [filteredFields])

  const tableState = useTableState<Row>()

  const getRowId = useCallback((row: Row) => row.id, [])

  return (
    <Table
      {...tableState}
      columns={columns}
      data={rows}
      getRowId={getRowId}
      emptyStateMessage="No results found"
      isLoading={isLoading}
    />
  )
}

export default React.memo(FieldsDisplayTable)
